import {
  CSNotification,
  formatCSTicketCategory,
  formatCSTicketState,
} from "@today/api/tracker"
import { classNames } from "@today/lib"
import dayjs from "dayjs"
import { IconType } from "react-icons"
import { FaTicket, FaArrowsRotate, FaComment } from "react-icons/fa6"

type NotificationItemProps = {
  notification: CSNotification
  onClick: (notification: CSNotification) => void
}

const typeConfig: Record<
  CSNotification["type"],
  {
    label: string
    Icon: IconType
    iconBg: string
    iconText: string
    tagBg: string
    tagText: string
  }
> = {
  NEW_TICKET: {
    label: "신규 티켓",
    Icon: FaTicket,
    iconBg: "bg-green-100",
    iconText: "text-green-600",
    tagBg: "bg-green-100",
    tagText: "text-green-700",
  },
  STATE_CHANGED: {
    label: "상태변경",
    Icon: FaArrowsRotate,
    iconBg: "bg-amber-100",
    iconText: "text-amber-600",
    tagBg: "bg-amber-100",
    tagText: "text-amber-700",
  },
  NEW_COMMENT: {
    label: "새 댓글",
    Icon: FaComment,
    iconBg: "bg-blue-100",
    iconText: "text-blue-600",
    tagBg: "bg-blue-100",
    tagText: "text-blue-700",
  },
}

export function NotificationItem({
  notification,
  onClick,
}: NotificationItemProps) {
  const config = typeConfig[notification.type]
  const { Icon } = config
  const detail = formatNotificationDetail(notification)
  const subText = formatNotificationSubText(notification)
  const timeAgo = dayjs(notification.createdAt).fromNow()

  return (
    <button
      className={classNames(
        "flex w-full cursor-pointer items-start gap-3 border-b border-gray-100 px-4 py-3 text-left transition-colors hover:bg-gray-50",
        !notification.isRead ? "bg-blue-50/40" : ""
      )}
      onClick={() => onClick(notification)}
    >
      {/* 아이콘 + 채널 뱃지 */}
      <div className="flex shrink-0 flex-col items-center gap-1">
        <div
          className={classNames(
            "flex h-8 w-8 items-center justify-center rounded-full",
            config.iconBg
          )}
        >
          <Icon className={config.iconText} size={14} />
        </div>
        <span className="rounded-full bg-gray-200 px-1.5 py-0.5 text-[9px] font-semibold leading-none text-gray-500">
          CS
        </span>
      </div>

      {/* 내용 */}
      <div className="min-w-0 flex-1">
        {/* 1줄: 타입 태그 + 송장번호 */}
        <div className="flex items-center gap-2">
          <span
            className={classNames(
              "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium leading-none",
              config.tagBg,
              config.tagText
            )}
          >
            {config.label}
          </span>
          <span
            className={classNames(
              "truncate text-sm",
              !notification.isRead
                ? "font-semibold text-gray-900"
                : "text-gray-700"
            )}
          >
            {notification.metadata.displayInvoiceNumber}
          </span>
        </div>

        {/* 2줄: 타입별 상세 */}
        <p className="mt-1 truncate text-sm text-gray-600">{detail}</p>

        {/* 3줄: 고객사/셀러 · 시간 */}
        <div className="mt-1 flex items-center gap-1 text-xs text-gray-400">
          {subText && (
            <>
              <span className="truncate">{subText}</span>
              <span>·</span>
            </>
          )}
          <span className="shrink-0">{timeAgo}</span>
        </div>
      </div>

      {/* 미읽음 도트 */}
      {!notification.isRead && (
        <div className="mt-2 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
      )}
    </button>
  )
}

function formatNotificationDetail(notification: CSNotification): string {
  switch (notification.type) {
    case "NEW_TICKET":
      return formatCSTicketCategory(notification.metadata.category)
    case "STATE_CHANGED":
      return `${formatCSTicketState(
        notification.metadata.oldState
      )} → ${formatCSTicketState(notification.metadata.newState)}`
    case "NEW_COMMENT":
      return `${notification.metadata.author} 님이 댓글을 남겼습니다`
  }
}

function formatNotificationSubText(notification: CSNotification): string {
  const { clientName, sellerName } = notification.metadata
  if (clientName && sellerName) {
    return `${clientName} / ${sellerName}`
  }
  return clientName || sellerName || ""
}

export function NotificationItemSkeleton() {
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-gray-200" />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <div className="h-4 w-14 animate-pulse rounded-full bg-gray-200" />
          <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
        </div>
        <div className="h-4 w-3/5 animate-pulse rounded bg-gray-200" />
        <div className="h-3 w-2/5 animate-pulse rounded bg-gray-200" />
      </div>
    </div>
  )
}
